import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import ServiceWorkerWrapper from "./components/ServiceWorkerWrapper";
// Load dev auth codes (adds console helpers in dev mode)
import "./config/devAuthCodes";
// iOS: reload once on chunk load failure (stale cache after deploy)
if (typeof window !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
  const key = "hd_ios_chunk_reload";
  window.addEventListener("unhandledrejection", function (e) {
    const msg = (e.reason && (e.reason.message || String(e.reason))) || "";
    if ((msg.includes("Loading chunk") || msg.includes("ChunkLoadError")) && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.location.reload();
    }
  });
}
// Load migration scripts (exposes window.migrateLLMPrompts for browser console)
// Also expose in demo environment (production build but still needs dev tools)
// Always try to load - the check happens at runtime inside the promise
import("@healthdesk/shared-services").then(services => {
  if (typeof window !== 'undefined') {
    // Runtime check for dev or demo environment
    const isDev = process.env.NODE_ENV === 'development';
    const hostname = window.location.hostname;
    const isDemo = hostname === 'hlthdsk-experimental-demo.web.app' ||
                   hostname === 'hlthdsk-experimental-demo.firebaseapp.com';
    
    if (!isDev && !isDemo) {
      // Not dev or demo, skip exposing dev tools
      return;
    }
    window.__db = services.db;
    window.__auth = services.auth || services.firebaseApp?.auth();
    window.__firebaseApp = services.firebaseApp;
    
    // Expose migration function for manual execution
    import("./scripts/migrateLLMPrompts").then(migrationModule => {
        window.runLLMMigration = async (overwrite = false) => {
          const { migrateLLMPrompts } = migrationModule;
          const { firebaseApp } = services;
          
          if (!services.db) {
            console.error('❌ Firebase db not available!');
            return;
          }
          
          // Get current authenticated user
          const user = firebaseApp.auth().currentUser;
          if (!user) {
            console.error('❌ No authenticated user. Please log in first.');
            return { error: 'not_authenticated' };
          }
          
          
          const results = await migrateLLMPrompts(services.db, user.uid, {
            overwriteExisting: overwrite,
            dryRun: false,
            verbose: true,
          });
          return results;
        };
    });

    // Expose diagnostic tests prompt update function
    import("./scripts/updateDiagnosticTestsPrompt").then(updateModule => {
        window.updateDiagnosticTestsPrompt = async (dryRun = false) => {
          const { updateDiagnosticTestsPrompt } = updateModule;
          const { firebaseApp } = services;
          
          if (!services.db) {
            console.error('❌ Firebase db not available!');
            return;
          }
          
          // Get current authenticated user
          const user = firebaseApp.auth().currentUser;
          if (!user) {
            console.error('❌ No authenticated user. Please log in first.');
            return { error: 'not_authenticated' };
          }
          
          
          const results = await updateDiagnosticTestsPrompt(services.db, user.uid, {
            dryRun,
            verbose: true,
          });
          return results;
        };
    });

    // Expose DDX prompt update function (adds supporting_factors requirement)
    import("./scripts/updateDDXPrompt").then(updateModule => {
        window.updateDDXPrompt = async (dryRun = false) => {
          const { updateDDXPrompt } = updateModule;
          const { firebaseApp } = services;
          
          if (!services.db) {
            console.error('❌ Firebase db not available!');
            return;
          }
          
          // Get current authenticated user
          const user = firebaseApp.auth().currentUser;
          if (!user) {
            console.error('❌ No authenticated user. Please log in first.');
            return { error: 'not_authenticated' };
          }
          
          
          const results = await updateDDXPrompt({
            dryRun,
            verbose: true,
            userId: user.uid,
          });
          return results;
        };
    });

    // Expose max_tokens fix function for chartmind-diagnostic-tests
    import("./scripts/fixDiagnosticTestsMaxTokens").then(fixModule => {
        const fixFn = fixModule.fixDiagnosticTestsMaxTokens || fixModule.default;
        if (fixFn) {
          window.fixDiagnosticTestsMaxTokens = async (options = {}) => {
            return await fixFn(options);
          };
        } else {
          console.error('❌ fixDiagnosticTestsMaxTokens function not found in module:', Object.keys(fixModule));
        }
    }).catch(err => {
      console.error('❌ Failed to load fixDiagnosticTestsMaxTokens:', err);
    });

    // Expose read-only Query Manager config (Firestore llmQueries)
    import("./scripts/getQueryManagerConfig").then(mod => {
      const fn = mod.getQueryManagerConfig || mod.default;
      if (fn) {
        window.getQueryManagerConfig = fn;
      }
    }).catch(() => {});
  }
}).catch(err => {
  console.warn('Could not expose Firebase to window:', err);
});

// One-time migration execution
// This will run the migration once when the app loads
// Set MIGRATION_COMPLETE in localStorage after first run
// Use safe localStorage access so iOS Safari (e.g. private mode) or restricted storage cannot block app load
if (typeof window !== 'undefined') {
  const MIGRATION_KEY = 'llm_prompts_migration_complete';
  const safeLocalStorageGet = (key) => {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  };
  const safeLocalStorageSet = (key, value) => {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  };
  const shouldRunMigration = !safeLocalStorageGet(MIGRATION_KEY);
  
  if (shouldRunMigration) {
    
    // Wait for Firebase to initialize and user to authenticate
    setTimeout(() => {
      Promise.all([
        import("@healthdesk/shared-services"),
        import("./scripts/migrateLLMPrompts"),
        import("@healthdesk/shared-hooks")
      ]).then(([services, migrationModule, hooks]) => {
        const { db, firebaseApp } = services;
        const { migrateLLMPrompts } = migrationModule;
        
        if (!db) {
          throw new Error('Firebase db is not available. Make sure Firebase is initialized.');
        }
        
        // Check if user is authenticated and has proper role
        return new Promise((resolve) => {
          firebaseApp.auth().onAuthStateChanged(async (user) => {
            if (!user) {
              console.info('[LLM prompt migration] No authenticated user; skipping auto-migration.');
              resolve({ skipped: true, reason: 'not_authenticated' });
              return;
            }
            
            
            // Check user role before attempting migration
            try {
              const userDoc = await db.collection('users').doc(user.uid).get();
              const userRole = userDoc.exists ? userDoc.data()?.role : null;
              
              if (userRole !== 'llm-manager-global') {
                console.warn('⚠️ User does not have llm-manager-global role. Skipping auto-migration.');
                resolve({ skipped: true, reason: 'insufficient_permissions', role: userRole });
                return;
              }
              
              
              const results = await migrateLLMPrompts(db, user.uid, {
                overwriteExisting: false,
                dryRun: false,
                verbose: false, // Reduce verbosity for auto-migration
              });
              resolve(results);
            } catch (error) {
              // Handle permission errors gracefully
              if (error.code === 'permission-denied' || error.message?.includes('permissions') || error.message?.includes('permission')) {
                console.warn('⚠️ Permission denied. User may not have llm-manager-global role.');
                resolve({ skipped: true, reason: 'permission_denied' });
                return;
              }
              
              console.error('❌ Migration error:', error);
              throw error;
            }
          });
        });
      }).then((results) => {
        if (results.skipped && results.reason === 'not_authenticated') {
          // Don't mark as complete if not authenticated
          return;
        }
        
        
        if (results.errors && results.errors.length > 0) {
          // Check if all errors are permission errors
          const allPermissionErrors = results.errors.every(err => 
            err.error?.includes('permission') || err.code === 'permission-denied'
          );
          
          if (allPermissionErrors) {
            console.warn('⚠️ Migration skipped due to permission errors.');
          } else {
            console.error('❌ Migration errors:', results.errors);
          }
          
          // Don't mark as complete if there were errors
          if ((results.migrated || 0) === 0) {
            return;
          }
        }
        
        // Mark migration as complete only if successful
        if ((results.migrated || 0) > 0 || (results.skipped || 0) > 0) {
          safeLocalStorageSet(MIGRATION_KEY, 'true');
          safeLocalStorageSet(`${MIGRATION_KEY}_timestamp`, new Date().toISOString());
          safeLocalStorageSet(`${MIGRATION_KEY}_results`, JSON.stringify({
            migrated: results.migrated || 0,
            skipped: results.skipped || 0,
            errors: (results.errors || []).length,
            timestamp: new Date().toISOString(),
          }));
          
        }
      }).catch((error) => {
        console.error('❌ Migration failed:', error);
        console.error('Error details:', error.stack);
      });
    }, 3000); // Wait 3 seconds for Firebase and auth to initialize
  } else {
    const results = safeLocalStorageGet(`${MIGRATION_KEY}_results`);
    if (results) {
      try {
        const data = JSON.parse(results);
      } catch (e) {
      }
    } else {
    }
  }
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <>
    <ServiceWorkerWrapper />
    <App />
  </>
);
